import React, { useState } from "react";
import { useSnackBar } from "../../../hooks/useSnackBar";
import "./Vehicles.css";

// components imports
import VehiclesTable from "../../../Components/BoltTable/BoltTable";
import SwitchButton from "../../../Components/SwitchButton/SwitchButton";
import Tooltip from "../../../Components/Tooltip/Tooltip";
import Confirm from "../../../Components/Popups/Confirm";
import VehicleForm from "./Components/VehicleForm/VehicleForm";
import BoltButton from "../../../Components/UI/Buttons/BoltButton";

// apis imports
import {
  deleteVehicle,
  fetchAllVehicles,
  changeStatus,
} from "../../../services/vehicles.services";

// library imports
import { FaRegEdit } from "react-icons/fa";
import { BsTrash } from "react-icons/bs";
import { FcPlus } from "react-icons/fc";
import { useTranslation } from "react-i18next";

const Vehicle = () => {
  const { t } = useTranslation("carrier");
  const [Change, setChange] = useState();
  const [manageVehicleModal, setManageVehicleModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteVehicleModal, setDeleteVehicleModal] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [refresh, setRefresh] = useState([]);

  const setSnack = useSnackBar();

  const columns = [
    {
      id: 0,
      label: "#",
      render: (row) => <>{row?.id ?? "-"}</>,
      width: "200px",
    },
    {
      id: 1,
      label: t("vehicles.active"),
      render: (row) => (
        <>
          <SwitchButton
            onChange={(e) => handleStatusChange(e, row)}
            checked={row.active === "true"}
            disabled={Change === "Status" ? true : false}
          />
        </>
      ),
      width: "200px",
    },
    {
      id: 2,
      label: t("vehicles.licensePlate"),
      render: (row) => <>{row?.license_plate ?? "-"}</>,
      width: "200px",
    },
    {
      id: 3,
      label: t("vehicles.year"),
      render: (row) => <>{row?.year ?? "-"}</>,
      width: "200px",
    },
    {
      id: 4,
      label: t("vehicles.type"),
      render: (row) => <>{row?.type ?? "-"}</>,
      width: "200px",
    },
    {
      id: 5,
      label: t("vehicles.make"),
      render: (row) => <>{row?.make ?? "-"}</>,
      width: "200px",
    },
    {
      id: 6,
      label: t("vehicles.model"),
      render: (row) => <>{row?.model ?? "-"}</>,
      width: "200px",
    },
    {
      id: 7,
      label: t("vehicles.color"),
      render: (row) => (
        <>
          <div
            className="dispscreen__tdata_vcolor "
            style={{ background: row?.color }}
          ></div>
        </>
      ),
      width: "200px",
    },
    {
      id: 8,
      label: t("vehicles.weight"),
      render: (row) => <>{`${row.weight_in_lbs} lb`}</>,
      width: "200px",
    },
    {
      id: 9,
      label: t("vehicles.volume"),
      render: (row) => <>{t("vehicles.volumeCbm", { volume: row.volume_in_cbm })}</>,
      width: "200px",
    },
    {
      id: 10,
      label: t("vehicles.actions"),
      render: (row) => (
        <div className="table_action__wrap">
          <Tooltip title={t("vehicles.editVehicleTitle")}>
            <div
              className="table_action__btn"
              onClick={() => setManageVehicleModal(row)}
            >
              <FaRegEdit size={7} />
              {t("common:actions.edit")}
            </div>
          </Tooltip>
          <Tooltip title={t("vehicles.deleteVehicle")}>
            <div
              className="table_action__btn"
              onClick={() => setDeleteVehicleModal(row.id)}
            >
              <BsTrash size={7} />
              {t("common:actions.delete")}
            </div>
          </Tooltip>
        </div>
      ),
      width: "200px",
      fixed: "right",
    },
  ];

  const deleteSingleVehicle = async () => {
    try {
      setLoading(true);
      const deleted = await deleteVehicle(deleteVehicleModal);
      if (deleted.error) {
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: deleted?.error?.message || t("vehicles.couldntDeleteVehicle"),
            severity: "error",
          };
        });
        setLoading(false);
      } else {
        setVehicles((prevData) => {
          return {
            ...prevData,
            data: prevData?.data?.filter(
              (vehicle) => vehicle.id !== deleteVehicleModal,
            ),
          };
        });
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: deleted?.data?.message,
            severity: "success",
          };
        });
        setLoading(false);
        setDeleteVehicleModal(false);
      }
    } catch (error) {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: error?.message || t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
      setLoading(false);
    }
  };

  const getAllVehicles = async (page, perPage, filter) => {
    const response = await fetchAllVehicles(page, perPage, filter);
    if (!response.error) {
      setVehicles(response?.data?.result);
    } else {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
    }
  };

  const handleStatusChange = async (e, record) => {
    try {
      setChange(true);
      setVehicles((prevData) => {
        return {
          ...prevData,
          data: prevData?.data?.map((item) =>
            item.id == record.id
              ? {
                  ...item,
                  active: e.target.checked ? "true" : "false",
                }
              : item,
          ),
        };
      });
      const updated = await changeStatus({
        record_id: parseInt(record.id),
        active: e.target.checked ? "true" : "false",
      });
      if (updated.error) {
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: updated.error.message,
            severity: "error",
          };
        });
      } else {
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: e.target.checked
              ? t("vehicles.statusActive")
              : t("vehicles.statusInactive"),
            severity: e.target.checked ? "success" : "info",
          };
        });
      }
      setChange(false);
    } catch (error) {
      setSnack((snack) => ({
        ...snack,
        open: true,
        text: error?.message || t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    }
  };

  return (
    <div
      className="dispatcher__screen"
      style={{ maxWidth: "100%", padding: "5px 5px" }}
    >
      <VehicleForm
        open={manageVehicleModal}
        setOpen={setManageVehicleModal}
        setSnack={setSnack}
        setRefresh={setRefresh}
      />
      <Confirm
        open={deleteVehicleModal}
        setOpen={setDeleteVehicleModal}
        handleConfirm={deleteSingleVehicle}
        text={t("vehicles.doYouWantToDelete")}
        loader={loading}
      />
      <div className="custom__table_size1 actionbtn__needtobe_fixwidth">
        <VehiclesTable
          tableTitle={t("vehicles.vehicles")}
          tableId="vehiclesTableForCarrier"
          message={t("vehicles.noVehiclesAvailableToDisplay")}
          searchPlaceHolder={t("vehicles.searchVehicles")}
          getAllData={getAllVehicles}
          data={vehicles}
          columns={columns}
          refresh={refresh}
          headerButtons={
            <div className="flex gap-1.5 flex-wrap items-center">
              <BoltButton
                icon={FcPlus}
                label={t("vehicles.addCaps")}
                onClick={() => setManageVehicleModal(true)}
                variant="success"
                size="sm"
              />
            </div>
          }
        />
      </div>
    </div>
  );
};

export default Vehicle;
